import { ImgID } from "../../../image_loader/img_id.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { setButtonTextOutline } from "../../common_ui_elements/button_text_outline.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIAdaptiveElementsByTextHorisontal } from "../../common_ui_elements/ui_adaptive_elements_by_text_horisontal.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { purchaseItem } from "../../../offline/economy_gateway.js";
import { adoptEconomyFromPlayer } from "../../../api/player_sync.js";
import { logVirtualCurrencySpent } from "../../../analytics/appsflyer_events.js";
import { ApiError } from "../../../api/http_client.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { Easing } from "../../../transforms/transform_base.js";
import { OfferService } from "../../../offers/offer_service.js";
import { OfferTriggerController } from "../../../offers/offer_trigger_controller.js";
import type { OfferDto } from "../../../api/dto/offers.dto.js";
import { CONTINUE_SPECIAL_OFFER_PLACEMENT, ContinueSpecialOfferPanel } from "./continue_special_offer_panel.js";
import { Vector2 } from "../../../common/vector2.js";
import type { ReviveOfferDto } from "../../../api/dto/player.dto.js";
import { OverlayFactory } from "../overlay_factory.js";
import { DialogCoinsBadge } from "../../common_ui_elements/dialog_coins_badge.js";
import { addMirroredDialogBg } from "../../common_ui_elements/mirrored_dialog_bg.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";

export interface ContinueOverlayParams
{
  offer: ReviveOfferDto;
  purchase_index: number;
  /** Level being played; reported with the continue_dialog_opened trigger event. */
  level?: number;
  onClose?: () => void;
  /**
   * The close button was tapped: true when the caller takes over (e.g. shows
   * another screen first and closes the dialog after it), false to close as usual.
   */
  onCloseButton?: () => boolean;
  onBuy?: ( shots: number ) => void;
  /**
   * Buys the balls instead of the level revive (`continue_balls`): resolves
   * with the balls bought, rejects when the purchase failed.
   */
  purchase?: () => Promise<number>;
  /** Placement of the special offer under the dialog; null shows none. */
  special_offer_placement?: string | null;
  /** Line under the offer's title when the offer sets none ("+{0} shots for this level" by default). */
  special_offer_description_key?: string;
  /** Shown for looks only while the placement has no server offer; it buys nothing. */
  special_offer_preview?: OfferDto;
  /**
   * An "i" button at the top left of the dialog, level with the close button
   * (Prize Shot: the prizes still in the shelf). Its tap shows what this builds,
   * given the button's rect in overlay coordinates; any next tap hides it.
   */
  info?: {
    img_id: ImgID;
    /** Share of the art the button itself fills (the rest is a clear margin); 1 by default. */
    fill?: number;
    tooltip: ( ctx: CanvasRenderingContext2D, anchor: ContinueOverlayAnchor ) => UIElement;
  };
  /**
   * Built ahead by OverlayFactory.prewarmOverlay, never shown: any offer goes
   * in as a preview and nothing is reported.
   */
  prewarm?: boolean;
}

export interface ContinueOverlayAnchor
{
  x: number;
  y: number;
  w: number;
  h: number;
}

export class ContinueOverlay extends AbstractOverlay
{
  private static readonly DESIGN_W = 909;
  private static readonly DESIGN_H = 1128;
  private static readonly BG_Y = 88;
  private static readonly BG_H = 1028;

  private static readonly SPECIAL_OFFER_BOTTOM_MARGIN = 120;
  private static readonly SPECIAL_OFFER_GAP = 59;
  private static readonly CLOSE_BTN_Y = 53;
  private static readonly CLOSE_BTN_X = 795;
  private static readonly CLOSE_BTN_SIZE = 106;
  /** Share of close_btn.png its round button fills (108 of 110 px). */
  private static readonly CLOSE_BTN_FILL = 108 / 110;

  private content_holder!: UIElementHolder;
  private purchase_inflight = false;
  private is_bought = false;
  private is_redirected_to_shop = false;
  private purchased_shots = 0;
  private coins_badge: DialogCoinsBadge | null = null;
  private buy_btn: UIElement | null = null;
  private special_offer_panel: ContinueSpecialOfferPanel | null = null;
  private content_shift_y = 0;
  private info_tooltip: UIElement | null = null;
  private info_dismiss: UIElementHolder | null = null;
  /** The close and "i" buttons: they pop in on every open. */
  private pop_buttons: UIElement[] = [];
  private static active: ContinueOverlay | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: ContinueOverlayParams,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    this.buildContent();

    if ( !params.prewarm )
      ContinueOverlay.active = this;
    this.requestSpecialOffer();
  }

  /** What the overlay is built from; the rest of the params only changes behaviour. */
  public static poolKeyFor( params: ContinueOverlayParams ): string
  {
    const placement = params.special_offer_placement === undefined ? CONTINUE_SPECIAL_OFFER_PLACEMENT : params.special_offer_placement;
    return [
      "ContinueOverlay",
      params.offer.price,
      params.offer.shots,
      placement ?? "-",
      params.special_offer_description_key ?? "-",
      params.info ? `${params.info.img_id}/${params.info.fill ?? 1}` : "-",
    ].join( ":" );
  }

  public override isPoolable(): boolean
  {
    return true;
  }

  public override poolKey(): string
  {
    return ContinueOverlay.poolKeyFor( this.params as ContinueOverlayParams );
  }

  public override onReuse( params: unknown ): void
  {
    const next = params as ContinueOverlayParams;
    if ( ContinueOverlay.poolKeyFor( next ) !== this.poolKey() )
      throw new Error( "ContinueOverlay: another layout" );

    this.params = next;
    this.purchase_inflight = false;
    this.is_bought = false;
    this.is_redirected_to_shop = false;
    this.purchased_shots = 0;
    this.hideInfoTooltip();
    if ( !next.prewarm )
      ContinueOverlay.active = this;

    // A real offer reports its impression when its panel is built, so only a
    // preview of the same offer stays; anything else is built anew.
    const preview = next.special_offer_preview;
    const placement = this.specialOfferPlacement();
    const resolved = placement === null ? null : OfferService.getPlacementOffer( placement );
    const keep_panel = !resolved && !!preview && !!this.special_offer_panel?.isPreviewOf( preview );
    if ( !keep_panel && this.special_offer_panel )
    {
      this.removeChild( this.special_offer_panel );
      this.special_offer_panel = null;
    }
    if ( !resolved && !preview && this.content_shift_y !== 0 )
    {
      this.content_shift_y = 0;
      this.removeAllChildren();
      this.buildContent();
    }
    else
    {
      this.refreshBuyButton();
      for ( const button of this.pop_buttons )
        ContinueOverlay.popIn( button );
    }

    this.requestSpecialOffer();
    this.invalidateSubtree();
  }

  private buildContent(): void
  {
    this.pop_buttons = [];
    this.createContentHolder();
    this.createBackground();
    this.createTitle();
    this.createDialogInfo();
    this.createCloseButton();
    this.createInfoButton();
    this.createCoinBadge();
    this.createBuyButton();
  }

  /** The level's Continue placement unless the caller names its own (or null for none). */
  private specialOfferPlacement(): string | null
  {
    const placement = ( this.params as ContinueOverlayParams ).special_offer_placement;
    return placement === undefined ? CONTINUE_SPECIAL_OFFER_PLACEMENT : placement;
  }

  /**
   * The special-offer placement under the dialog comes either from the
   * catalog (no trigger rules) or from a trigger decision on
   * continue_dialog_opened; the latter arrives asynchronously through
   * presentSpecialOffer.
   */
  private requestSpecialOffer(): void
  {
    const placement = this.specialOfferPlacement();
    if ( placement === null ) return;
    const resolved = OfferService.getPlacementOffer( placement );
    const preview = ( this.params as ContinueOverlayParams ).special_offer_preview;
    if ( ( this.params as ContinueOverlayParams ).prewarm )
    {
      const offer = resolved?.offer ?? preview;
      if ( offer ) this.showSpecialOffer( offer, undefined, true );
      return;
    }
    if ( resolved ) this.showSpecialOffer( resolved.offer );
    else if ( preview && !this.special_offer_panel?.isPreviewOf( preview ) ) this.showSpecialOffer( preview, undefined, true );
    if ( placement !== CONTINUE_SPECIAL_OFFER_PLACEMENT ) return;
    const level = ( this.params as ContinueOverlayParams ).level;
    void OfferTriggerController.report( 'continue_dialog_opened', level !== undefined ? { level } : {} );
  }

  /** Entry point for a trigger decision; false when no Continue dialog is up. */
  public static presentSpecialOffer( offer: OfferDto, trigger_event_id: string ): boolean
  {
    const overlay = ContinueOverlay.active;
    if ( !overlay || overlay.is_bought || overlay.specialOfferPlacement() !== CONTINUE_SPECIAL_OFFER_PLACEMENT ) return false;
    overlay.showSpecialOffer( offer, trigger_event_id );
    return true;
  }

  private showSpecialOffer( offer: OfferDto, trigger_event_id?: string, preview = false ): void
  {
    if ( this.special_offer_panel )
    {
      this.removeChild( this.special_offer_panel );
      this.special_offer_panel = null;
    }
    const panel_w = LayoutScaler.widthFromDesign( ContinueSpecialOfferPanel.DESIGN_W );
    const panel_h = LayoutScaler.heightFromDesign( ContinueSpecialOfferPanel.DESIGN_H );
    const gap = LayoutScaler.heightFromDesign( ContinueOverlay.SPECIAL_OFFER_GAP );
    const x = this.rect.getW2() - panel_w / 2;
    const y = this.rect.getH() - panel_h - LayoutScaler.heightFromDesign( ContinueOverlay.SPECIAL_OFFER_BOTTOM_MARGIN );
    const holder_h = LayoutScaler.heightFromDesign( ContinueOverlay.DESIGN_H );
    const bg_bottom = LayoutScaler.heightFromDesign( ContinueOverlay.BG_Y + ContinueOverlay.BG_H );
    const centered_holder_y = this.rect.getH2() - holder_h / 2;
    const overflow = centered_holder_y + bg_bottom + gap - y;
    const shift = Math.max( 0, Math.min( overflow, centered_holder_y - gap ) );
    if ( shift !== this.content_shift_y )
    {
      this.content_shift_y = shift;
      this.removeAllChildren();
      this.info_tooltip = null;
      this.info_dismiss = null;
      this.buildContent();
    }
    this.special_offer_panel = new ContinueSpecialOfferPanel( this.ctx, x, y, {
      offer,
      trigger_event_id,
      placement: this.specialOfferPlacement() ?? undefined,
      description_key: ( this.params as ContinueOverlayParams ).special_offer_description_key,
      preview,
      on_purchased: ( level_shots: number ) =>
      {
        if ( level_shots <= 0 )
        {
          // Inventory only: nothing to resume with, the player keeps deciding.
          this.syncCoins();
          return;
        }
        this.is_bought = true;
        this.purchased_shots = level_shots;
        OverlayManager.closeOverlay();
      },
    } );
    this.addChild( this.special_offer_panel );
    this.invalidateSubtree();
  }

  private createContentHolder(): void
  {
    const holder_w = LayoutScaler.widthFromDesign( ContinueOverlay.DESIGN_W );
    const holder_h = LayoutScaler.heightFromDesign( ContinueOverlay.DESIGN_H );
    const holder_x = this.rect.getW2() - holder_w / 2;
    const holder_y = this.rect.getH2() - holder_h / 2 - this.content_shift_y;

    this.content_holder = new UIElementHolder( this.ctx, holder_x, holder_y, holder_w, holder_h );
    this.addChild( this.content_holder );
  }

  private createBackground(): void
  {
    const design_bg_x = 0;

    addMirroredDialogBg(
      this.ctx, this.content_holder,
      design_bg_x, ContinueOverlay.BG_Y, ContinueOverlay.DESIGN_W, ContinueOverlay.BG_H
    );
  }

  private createTitle(): void
  {
    const design_title_x = 0;
    const design_title_y = 178;
    const design_title_h = 136;
    const design_font_size = 100;
    const design_outline_w = 18;

    const title_full_w = LayoutScaler.widthFromDesign( ContinueOverlay.DESIGN_W );
    const MAGIC_NUM_CORRECTION = 0.86;
    const title_w = title_full_w * MAGIC_NUM_CORRECTION;

    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_title_x ) + ( title_full_w - title_w ) / 2,
      LayoutScaler.yFromDesign( design_title_y ),
      title_w,
      LayoutScaler.heightFromDesign( design_title_h ),
      {
        localization_key: "CONTINUE_DIALOG.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.booster_dialog_title_outline,
        outline_offset_y: LayoutScaler.yFromDesign( 3 ),
        scale_to_width: true,
        shadow_blur: LayoutScaler.widthFromDesign( 10 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 5 ),
        shadow_offset_x: LayoutScaler.xFromDesign( 0 ),
        auto_wrap: false,
      }
    );
    this.content_holder.addChild( title );
  }

  private createDialogInfo(): void
  {
    const shots = ( this.params as ContinueOverlayParams ).offer.shots;
    const design_card_w = 777;
    const design_card_h = 395;
    const design_card_x = ( ContinueOverlay.DESIGN_W - design_card_w ) / 2;
    const design_card_y = 338;

    const card_w = LayoutScaler.widthFromDesign( design_card_w );
    const card_h = LayoutScaler.heightFromDesign( design_card_h );
    const card_x = LayoutScaler.xFromDesign( design_card_x );
    const card_y = LayoutScaler.yFromDesign( design_card_y );

    const card = new UIImage( this.ctx, card_x, card_y, card_w, card_h, ImgID.UI_PaternedBg );
    this.content_holder.addChild( card );

    const design_bonus_y = 31;
    const design_bonus_h = 246;
    const design_bonus_font_size = 180;
    const design_bonus_halo_outline_w = 50;
    const design_bonus_outline_w = 20;

    const bonus_y = LayoutScaler.yFromDesign( design_bonus_y );
    const bonus_h = LayoutScaler.heightFromDesign( design_bonus_h );

    const bonus_text_halo = new UITextElement(
      this.ctx,
      0,
      bonus_y,
      card_w,
      bonus_h,
      {
        text: `+${shots}`,
        font_size: LayoutScaler.textSizeFromDesign( design_bonus_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        outline_color: ColorScheme.white,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_bonus_halo_outline_w ),
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    card.addChild( bonus_text_halo );

    const bonus_text = new UITextElement(
      this.ctx,
      0,
      bonus_y,
      card_w,
      bonus_h,
      {
        text: `+${shots}`,
        font_size: LayoutScaler.textSizeFromDesign( design_bonus_font_size ),
        font_weight: 900,
        text_color: ColorScheme.continue_overlay_bonus_text,
        outline_color: ColorScheme.booster_dialog_title_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_bonus_outline_w ),
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    card.addChild( bonus_text );

    const design_caption_y = 270;
    const design_caption_h = 90;
    const design_caption_font_size = 54;

    const MAGIC_NUM_CORRECTION = 0.86;
    const caption_w = card_w * MAGIC_NUM_CORRECTION;

    const caption = new UITextElement(
      this.ctx,
      ( card_w - caption_w ) / 2,
      LayoutScaler.yFromDesign( design_caption_y ),
      caption_w,
      LayoutScaler.heightFromDesign( design_caption_h ),
      {
        localization_key: "CONTINUE_OVERLAY.DYNAMIC_TEXT",
        text_variable_params: [ String( shots ) ],
        color_map: { highlight: ColorScheme.continue_overlay_caption_highlight },
        font_size: LayoutScaler.textSizeFromDesign( design_caption_font_size ),
        font_weight: 900,
        text_color: ColorScheme.menu_top_el_text,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        outline_color: ColorScheme.white,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        vertical_align: MyTextVerticalAlign.Center,
        shadow_offset_x: 0,
        shadow_offset_y: 0,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    card.addChild( caption );
  }

  private createCloseButton(): void
  {
    const design_btn_size = ContinueOverlay.CLOSE_BTN_SIZE;
    const design_btn_x = ContinueOverlay.CLOSE_BTN_X;
    const design_btn_y = ContinueOverlay.CLOSE_BTN_Y;

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      LayoutScaler.widthFromDesign( design_btn_size ),
      LayoutScaler.heightFromDesign( design_btn_size ),
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () =>
    {
      if ( ( this.params as ContinueOverlayParams ).onCloseButton?.() ) return;
      OverlayManager.closeOverlay();
    } );
    this.content_holder.addChild( btn );
    this.pop_buttons.push( btn );
    ContinueOverlay.popIn( btn );
  }

  /** Grows past full size and settles. */
  private static popIn( btn: UIElement ): void
  {
    const btn_start   = performance.now();
    const btn_up_dur  = 176;
    const btn_set_dur = 112;

    btn.stopAllTransforms();
    btn.setScale( new Vector2( 0, 0 ) );

    btn.addTransform( new TransformScale(
      btn, true,
      new Vector2( 0, 0 ), new Vector2( 1.25, 1.25 ),
      btn_start, btn_up_dur,
      { easing: Easing.EaseOutQuad }
    ) );

    btn.addTransform( new TransformScale(
      btn, true,
      new Vector2( 1.25, 1.25 ), new Vector2( 1.0, 1.0 ),
      btn_start + btn_up_dur, btn_set_dur,
      { easing: Easing.EaseOutQuad }
    ) );
  }

  /** Mirrors the close button: same size and height, as far from the left edge as it is from the right. */
  private createInfoButton(): void
  {
    const info = ( this.params as ContinueOverlayParams ).info;
    if ( !info ) return;
    // Centred where the close button's mirror image is; sized so the visible
    // circle matches it (the caller's art may carry a clear margin, `fill`).
    const design_size = ContinueOverlay.CLOSE_BTN_SIZE * ContinueOverlay.CLOSE_BTN_FILL / ( info.fill ?? 1 );
    const design_cx = ContinueOverlay.DESIGN_W - ContinueOverlay.CLOSE_BTN_X - ContinueOverlay.CLOSE_BTN_SIZE / 2;
    const design_cy = ContinueOverlay.CLOSE_BTN_Y + ContinueOverlay.CLOSE_BTN_SIZE / 2;
    const size = LayoutScaler.widthFromDesign( design_size );
    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_cx - design_size / 2 ),
      LayoutScaler.yFromDesign( design_cy - design_size / 2 ),
      size,
      size,
      info.img_id,
      undefined,
      true
    );
    btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.showInfoTooltip( btn );
    } );
    this.content_holder.addChild( btn );
    this.pop_buttons.push( btn );
    ContinueOverlay.popIn( btn );
  }

  private showInfoTooltip( btn: UIElement ): void
  {
    const info = ( this.params as ContinueOverlayParams ).info;
    if ( !info ) return;
    this.hideInfoTooltip();

    const holder = this.content_holder.pixelPosImportant();
    const pos = btn.pixelPosImportant();
    const anchor = { x: holder.x + pos.x, y: holder.y + pos.y, w: btn.getRect().getW(), h: btn.getRect().getH() };

    // Any tap, the "i" itself included, puts it away again.
    this.info_dismiss = new UIElementHolder( this.ctx, 0, 0, this.rect.getW(), this.rect.getH() );
    this.info_dismiss.setOnClick( () => this.hideInfoTooltip() );
    this.addChild( this.info_dismiss );
    this.info_tooltip = info.tooltip( this.ctx, anchor );
    this.info_tooltip.setOnClick( () => this.hideInfoTooltip() );
    this.addChild( this.info_tooltip );
    this.invalidateSubtree();
  }

  private hideInfoTooltip(): void
  {
    if ( this.info_tooltip ) this.removeChild( this.info_tooltip );
    if ( this.info_dismiss ) this.removeChild( this.info_dismiss );
    this.info_tooltip = null;
    this.info_dismiss = null;
    this.invalidateSubtree();
  }

  private createCoinBadge(): void
  {
    // Coins bought in the shop may now cover the price: rebuild the buy button.
    this.coins_badge = new DialogCoinsBadge( this.ctx, this, () => this.refreshBuyButton() );
    this.addChild( this.coins_badge );
  }

  private createBuyButton(): void
  {
    const price = ( this.params as ContinueOverlayParams ).offer.price;
    const design_btn_w = 660;
    const design_btn_h = 265;
    const design_btn_y = 760;
    const design_btn_x = ( ContinueOverlay.DESIGN_W - design_btn_w ) / 2;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );
    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );

    const btn = new UINineSliceElement(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const design_text_font_size = 64;
    const design_text_outline_w = 8;
    const design_text_outline_offset_y = 2;

    const text_options = {
      font_size: LayoutScaler.textSizeFromDesign( design_text_font_size ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( design_text_outline_w ),
      outline_offset_y: LayoutScaler.yFromDesign( design_text_outline_offset_y ),
      ...TextStyleScheme.buttonShadow(),
    };

    const buy_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "BUY",
        ...text_options,
      }
    );

    const design_icon_size = 104;
    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const icon = new UIImage(
      this.ctx, 0, 0, icon_size, icon_size, ImgID.IconCoin
    );

    const price_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        text: price.toString(),
        ...text_options,
      }
    );

    const design_content_gap = 0;

    const content = new UIAdaptiveElementsByTextHorisontal(
      this.ctx, 0, 0, btn_w, btn_h,
      [ buy_text, icon, price_text ],
      LayoutScaler.widthFromDesign( design_content_gap )
    );
    btn.addChild( content, true );

    const setActive = ( active: boolean ): void =>
    {
      btn.setImgId( active ? ImgID.UI_GreenFrameBtn : ImgID.UI_NonActiveBtn, active ? "frame_btn" : "non_active_btn" );
      setButtonTextOutline( [ buy_text, price_text ], active, ColorScheme.more_lives_buy_outline );
      btn.invalidateSubtree();
    };

    const can_afford = StatisticsStorage.load().coins >= price;

    if ( can_afford )
    {
      btn.setOnClick( () =>
      {
        if ( this.purchase_inflight )
          return;

        this.purchase_inflight = true;
        setActive( false );

        this.purchaseShots()
          .then( shots =>
          {
            this.is_bought = true;
            this.purchased_shots = shots;
            OverlayManager.closeOverlay();
          } )
          .catch( e =>
          {
            console.warn( '[Economy] continue purchase failed', e );
            this.purchase_inflight = false;
            const sold_out = e instanceof ApiError && e.status === 409;
            btn.setClickable( !sold_out );
            setActive( !sold_out );
          } );
      } );
    }
    else
    {
      btn.setOnClick( () =>
      {
        console.log('ContinueOverlay: buy button clicked');
        // OverlayManager.closeOverlay();
        OverlayFactory.openOverlay( OverlayId.SHOP_OVERLAY, {
          onShopClosed: () => this.refreshBuyButton()
        } );
      } );
    }

    this.content_holder.addChild( btn );
    this.buy_btn = btn;
  }

  private purchaseShots(): Promise<number>
  {
    const params = this.params as ContinueOverlayParams;
    if ( params.purchase )
      return params.purchase();

    return purchaseItem( "continue_balls", {
      purchase_index: params.purchase_index,
      price: params.offer.price,
      qty: params.offer.shots,
    } ).then( r =>
    {
      adoptEconomyFromPlayer( r.player );
      void logVirtualCurrencySpent( {
        currency_name: 'coins',
        amount: r.purchased.price,
        sink: 'continue_balls',
      } );
      return r.purchased.qty;
    } );
  }

  private refreshBuyButton(): void
  {
    this.syncCoins();
    if ( this.buy_btn )
      this.content_holder.removeChild( this.buy_btn );
    this.createBuyButton();
  }

  public override onOverlayWillClose(): void
  {
    if ( ContinueOverlay.active === this ) ContinueOverlay.active = null;
    this.special_offer_panel?.trackClosed( this.is_bought ? 'revive_purchased' : this.is_redirected_to_shop ? 'redirected_to_shop' : 'dismissed' );
    if ( this.is_bought )
    {
      console.log('ContinueOverlay.onOverlayWillClose() is_bought');
      ( this.params as ContinueOverlayParams )?.onBuy?.( this.purchased_shots );
      return;
    }

    if ( this.is_redirected_to_shop )
      return;

    console.log('ContinueOverlay.onOverlayWillClose() is_redirected_to_shop');
    ( this.params as ContinueOverlayParams )?.onClose?.();
  }

  public name(): string
  {
    return "ContinueOverlay";
  }

  private syncCoins(): void
  {
    this.coins_badge?.sync( performance.now() );
  }

  public override update( now: number ): void
  {
    super.update( now );
    this.coins_badge?.sync( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
